import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Divider, 
  Row, 
  Col, 
  InputNumber, 
  Switch, 
  Button, 
  Typography, 
  Spin, 
  Form, 
  Space,
  message
} from 'antd';
import api from '../../../services/api';

const PasswordPolicy = ({ settings, loading, onUpdate }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Initialize form with default values
  const initialValues = {
    min_length: 8,
    require_uppercase: true,
    require_lowercase: true,
    require_digit: true,
    require_special_char: true,
    max_age_days: 90,
    history_size: 5,
    max_attempts: 5,
    lockout_duration_minutes: 15,
  };
  
  // Set form values when settings are loaded
  useEffect(() => {
    if (settings) {
      const formValues = {
        ...initialValues,
        ...settings
      };
      
      // Ensure boolean values are properly set
      const formValuesToSet = {
        min_length: formValues.min_length || 8,
        require_uppercase: formValues.require_uppercase !== false, // Default to true if not set
        require_lowercase: formValues.require_lowercase !== false, // Default to true if not set
        require_digit: formValues.require_digit !== false, // Default to true if not set
        require_special_char: formValues.require_special_char !== false, // Default to true if not set
        max_age_days: formValues.max_age_days || 90,
        history_size: formValues.history_size || 5,
        max_attempts: formValues.max_attempts || 5,
        lockout_duration_minutes: formValues.lockout_duration_minutes || 15
      };
      
      form.setFieldsValue(formValuesToSet);
      setHasChanges(false);
    }
  }, [settings, form]);
  
  const onValuesChange = (changedValues, allValues) => {
    // Get current form values
    const currentValues = form.getFieldsValue(true);
    
    // Check if any field has changed from initial values
    const changed = Object.keys(currentValues).some(key => {
      const currentValue = currentValues[key];
      const initialValue = settings?.[key] ?? initialValues[key];
      
      // Special handling for boolean values to ensure consistent comparison
      if (typeof initialValue === 'boolean' || typeof currentValue === 'boolean') {
        return Boolean(initialValue) !== Boolean(currentValue);
      }
      
      return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
    });
    
    setHasChanges(changed);
  };
  
  const handleSubmit = async () => {
    try {
      // Validate form fields
      const values = await form.validateFields();
      
      // Ensure we have all required fields with defaults
      const passwordPolicy = {
        min_length: values.min_length || 8,
        require_uppercase: values.require_uppercase !== false, // Default to true if not set
        require_lowercase: values.require_lowercase !== false, // Default to true if not set
        require_digit: values.require_digit !== false, // Default to true if not set
        require_special_char: values.require_special_char !== false, // Default to true if not set
        max_age_days: values.max_age_days || 90,
        history_size: values.history_size || 5,
        max_attempts: values.max_attempts || 5,
        lockout_duration_minutes: values.lockout_duration_minutes || 15
      };
      
      setSaving(true);
      
      try {
        // Call the API to update the password policy
        await api.settings.passwordPolicy.update(passwordPolicy);
        
        // Show success message
        message.success('Password policy updated successfully');
        
        // Reset the form's dirty state
        setHasChanges(false);
        
        // Update the settings in the parent component
        if (typeof onUpdate === 'function') {
          onUpdate();
        }
      } catch (error) {
        console.error('API Error:', error);
        const errorMessage = error.response?.data?.message || 'Failed to update password policy';
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Handle validation errors (already shown by Ant Design)
      if (error.errorFields) {
        return;
      }
      
      console.error('Error updating password policy:', error);
      
      // Show user-friendly error message
      const errorMessage = error.response?.data?.detail || 
                         error.message || 
                         'Failed to update password policy. Please try again.';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = () => {
    if (settings) {
      form.setFieldsValue(settings);
    } else {
      form.resetFields();
    }
    setHasChanges(false);
  };
  
  if (loading && !settings) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }
  
  return (
    <Card 
      title="Password Policy"
      extra={
        <Typography.Text type="secondary">
          Configure password requirements and security settings
        </Typography.Text>
      }

    >
      <Divider />
      <Form 
        form={form}
        layout="vertical" 
        onValuesChange={onValuesChange}
        initialValues={initialValues}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="min_length"
              label="Minimum Password Length"
              rules={[
                { required: true, message: 'Please enter minimum password length' },
                { type: 'number', min: 6, max: 64, message: 'Must be between 6 and 64' }
              ]}
              help="Minimum length for user passwords (6-64)"
            >
              <InputNumber
                min={6}
                max={64}
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            <Form.Item
              name="max_age_days"
              label="Password Expiration (Days)"
              rules={[
                { required: true, message: 'Please enter password expiration days' },
                { type: 'number', min: 1, max: 365, message: 'Must be between 1 and 365 days' }
              ]}
              help="Number of days until password expires (1-365)"
            >
              <InputNumber
                min={1}
                max={365}
                style={{ width: '100%' }}
                addonAfter="days"
              />
            </Form.Item>
            
            <Form.Item
              name="history_size"
              label="Password History Size"
              rules={[
                { required: true, message: 'Please enter password history size' },
                { type: 'number', min: 0, max: 24, message: 'Must be between 0 and 24' }
              ]}
              help="Number of previous passwords to remember (0-24)"
            >
              <InputNumber
                min={0}
                max={24}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
            
          <Col xs={24} md={12}>
            <div style={{ marginBottom: '16px' }}>
              <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                Password Requirements
              </Typography.Title>
              
              <Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Form.Item 
                      name="require_uppercase"
                      valuePropName="checked"
                      noStyle
                      rules={[
                        { required: true, message: 'This field is required' },
                        { type: 'boolean' }
                      ]}
                    >
                      <Switch 
                        checkedChildren="On" 
                        unCheckedChildren="Off"
                      />
                    </Form.Item>
                    <span style={{ marginLeft: 8 }}>Require at least one uppercase letter (A-Z)</span>
                  </Space>
                  
                  <Space style={{ marginTop: 8 }}>
                    <Form.Item 
                      name="require_lowercase"
                      valuePropName="checked"
                      noStyle
                      rules={[
                        { required: true, message: 'This field is required' },
                        { type: 'boolean' }
                      ]}
                    >
                      <Switch 
                        checkedChildren="On" 
                        unCheckedChildren="Off"
                      />
                    </Form.Item>
                    <span style={{ marginLeft: 8 }}>Require at least one lowercase letter (a-z)</span>
                  </Space>
                  
                  <Space style={{ marginTop: 8 }}>
                    <Form.Item 
                      name="require_digit"
                      valuePropName="checked"
                      noStyle
                      rules={[
                        { required: true, message: 'This field is required' },
                        { type: 'boolean' }
                      ]}
                    >
                      <Switch 
                        checkedChildren="On" 
                        unCheckedChildren="Off"
                      />
                    </Form.Item>
                    <span style={{ marginLeft: 8 }}>Require at least one digit (0-9)</span>
                  </Space>
                  
                  <Space style={{ marginTop: 8 }}>
                    <Form.Item 
                      name="require_special_char"
                      valuePropName="checked"
                      noStyle
                      rules={[
                        { required: true, message: 'This field is required' },
                        { type: 'boolean' }
                      ]}
                    >
                      <Switch 
                        checkedChildren="On" 
                        unCheckedChildren="Off"
                      />
                    </Form.Item>
                    <span style={{ marginLeft: 8 }}>Require at least one special character (!@#$%^&*)</span>
                  </Space>
                  
                </Space>
              </Form.Item>
              
              <Divider>Account Lockout</Divider>
              
              <Form.Item
                name="max_attempts"
                label="Maximum Failed Login Attempts"
                rules={[
                  { required: true, message: 'Please enter maximum failed attempts' },
                  { type: 'number', min: 1, max: 10, message: 'Must be between 1 and 10' }
                ]}
                help="Number of failed login attempts before account is locked (1-10)"
              >
                <InputNumber
                  min={1}
                  max={10}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              
              <Form.Item
                name="lockout_duration_minutes"
                label="Account Lockout Duration (Minutes)"
                rules={[
                  { required: true, message: 'Please enter lockout duration' },
                  { type: 'number', min: 1, max: 1440, message: 'Must be between 1 and 1440 minutes (24 hours)' }
                ]}
                help="Duration in minutes an account remains locked after too many failed attempts (1-1440)"
              >
                <InputNumber
                  min={1}
                  max={1440}
                  style={{ width: '100%' }}
                  addonAfter="minutes"
                />
              </Form.Item>
            </div>
          </Col>
          
          <Col span={24} style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                htmlType="submit"
                onClick={handleSubmit}
                // disabled={!hasChanges}
                loading={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Col>
        </Row>
        
      </Form>
    </Card>
  );
};

export default PasswordPolicy;
